import React, { useContext, useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Switch, Button, Card, Row, Col, Space, message } from 'antd';
import { SaveOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';

const { Option } = Select;
const { TextArea } = Input;

function CouponDetail() {
  const { id } = useParams();
  const couponId = id;
  const navigate = useNavigate();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const { doFetch: fetchCoupon } = useFetch();
  const { doFetch: updateCouponFetch } = useFetch();

  useEffect(() => {
    setHeaderComponent(`Edit Coupon: ${couponId}`);

    return () => {
      setHeaderComponent(null);
    };
  }, [setHeaderComponent, couponId]);

  useEffect(() => {
    if (couponId && couponId !== 'undefined' && couponId !== 'null') {
      loadCoupon();
    } else {
      console.error('Invalid coupon ID:', couponId);
      message.error('Invalid coupon ID');
      navigate('/coupons');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId]);

  const loadCoupon = () => {
    if (!couponId || couponId === 'undefined' || couponId === 'null') {
      console.error('Cannot load coupon: invalid coupon ID');
      return;
    }

    setLoading(true);
    fetchCoupon({
      url: URLS.getCoupon({ couponId }),
      method: 'GET',
      onSuccess: (data) => {
        const coupon = data.coupon || data;
        form.setFieldsValue({
          code: coupon.code,
          discount_type: coupon.discount_type,
          discount_value: coupon.discount_value,
          currency: coupon.currency || 'USD',
          description: coupon.description,
          valid_until: coupon.expires_at ? dayjs(coupon.expires_at) : null,
          max_usage: coupon.usage_limit,
          min_order_amount: coupon.minimum_order_amount,
          is_active: coupon.is_active !== undefined ? coupon.is_active : true,
        });
        setLoading(false);
      },
      onError: (error) => {
        console.error('Failed to load coupon:', error);
        message.error('Failed to load coupon details');
        setLoading(false);
      },
    });
  };

  const handleSubmit = async (values) => {
    const formData = {
      code: values.code,
      discount_type: values.discount_type,
      discount_value: values.discount_value,
      currency: values.currency || 'USD',
      description: values.description,
      expires_at: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : null,
      usage_limit: values.max_usage || null,
      minimum_order_amount: values.min_order_amount || null,
      is_active: values.is_active !== undefined ? values.is_active : true,
    };

    updateCouponFetch({
      url: URLS.updateCoupon({ couponId }),
      method: 'PUT',
      params: formData,
      onSuccess: () => {
        message.success('Coupon updated successfully!');
        navigate('/coupons');
      },
      onError: (error) => {
        message.error('Failed to update coupon. Please try again.');
        console.error('Error updating coupon:', error);
      },
      showSuccessNotification: true,
      successMessage: 'Coupon updated successfully',
    });
  };

  const handleCancel = () => {
    navigate('/coupons');
  };

  if (loading) {
    return <div>Loading coupon details...</div>;
  }

  return (
    <Row justify="center">
      <Col xs={24} sm={22} md={20} lg={16} xl={12}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="code"
              label="Coupon Code"
              rules={[
                { required: true, message: 'Please enter a coupon code' },
                { pattern: /^[A-Z0-9_-]+$/, message: 'Code must contain only uppercase letters, numbers, hyphens, and underscores' },
              ]}
            >
              <Input
                placeholder="e.g., SAVE20"
                onChange={(e) => {
                  const upperValue = e.target.value.toUpperCase();
                  form.setFieldsValue({ code: upperValue });
                }}
              />
            </Form.Item>

            <Form.Item
              name="discount_type"
              label="Discount Type"
              rules={[{ required: true, message: 'Please select a discount type' }]}
            >
              <Select>
                <Option value="percentage">Percentage</Option>
                <Option value="fixed">Fixed Amount</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="discount_value"
              label="Discount Value"
              rules={[
                { required: true, message: 'Please enter discount value' },
                { type: 'number', min: 0, message: 'Discount value must be positive' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter discount value"
                min={0}
                formatter={(value) => {
                  if (!value) return '';
                  const discountType = form.getFieldValue('discount_type');
                  return discountType === 'percentage' ? `${value}%` : value;
                }}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.discount_type !== currentValues.discount_type}
            >
              {({ getFieldValue }) =>
                getFieldValue('discount_type') === 'fixed' ? (
                  <Form.Item
                    name="currency"
                    label="Currency (for fixed amount)"
                  >
                    <Select>
                      <Option value="USD">USD</Option>
                      <Option value="EUR">EUR</Option>
                      <Option value="GBP">GBP</Option>
                      <Option value="IQD">IQD</Option>
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={3} placeholder="Optional description for this coupon" />
            </Form.Item>

            <Form.Item
              name="valid_until"
              label="Expires At"
              rules={[{ required: true, message: 'Please select expiration date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select expiration date"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="max_usage"
                  label="Maximum Usage"
                  tooltip="Leave empty for unlimited usage"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Unlimited"
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="min_order_amount"
                  label="Minimum Order Amount"
                  tooltip="Minimum order amount required to use this coupon"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="No minimum"
                    min={0}
                    step={0.01}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="is_active"
              label="Active Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="large">
                  Update Coupon
                </Button>
                <Button icon={<LeftOutlined />} onClick={handleCancel} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
}

export default CouponDetail;

